import React, { useEffect, useMemo, useState } from "react";
import type { TimeEntry, UserProfile } from "../../../shared/types";
import { QuickActionButton } from "../components/dashboard";
import { ConfirmationDialog } from "../components/ConfirmationDialog";
import { PageHeader, Toast } from "../components/common";
import { CreateManualTimeEntryModal } from "../components/modals";
import { BORDER_RADIUS, COLORS, SPACING, TYPOGRAPHY } from "../constants/theme";
import { useAuth } from "../hooks/useAuth";
import { useToast } from "../hooks/useToast";
import {
  approveTimeEntry,
  bulkApproveTimeEntries,
  getStaffTimeEntries,
  reopenTimeEntry,
} from "../services/timeEntry.service";
import { getManagerStaff, getCompanyUsers } from "../services/user.service";
import { SELECT_CHEVRON_STYLE } from "../styles/components";

function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function getDefaultDateRange() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return { start: toLocalDateString(start), end: toLocalDateString(end) };
}

function Flag({
  label,
  color,
}: {
  label: string;
  color: { light: string; dark: string };
}) {
  return (
    <span
      style={{
        padding: "2px 6px",
        borderRadius: 4,
        fontSize: TYPOGRAPHY.fontSize.xs,
        fontWeight: TYPOGRAPHY.fontWeight.medium,
        backgroundColor: color.light,
        color: color.dark,
      }}
    >
      {label}
    </span>
  );
}

function DetailBlock({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div style={{ gridColumn: wide ? "span 2" : undefined }}>
      <div
        style={{
          fontSize: TYPOGRAPHY.fontSize.xs,
          fontWeight: TYPOGRAPHY.fontWeight.semibold,
          color: COLORS.text.tertiary,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      <div
        style={{ fontSize: TYPOGRAPHY.fontSize.sm, color: COLORS.text.primary }}
      >
        {children}
      </div>
    </div>
  );
}

export function TimeEntries() {
  const { currentUser } = useAuth();
  const tz = currentUser?.companyTimezone;
  const { toasts, showToast, dismissToast } = useToast();
  const [staff, setStaff] = useState<UserProfile[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedEntries, setSelectedEntries] = useState<Set<string>>(
    new Set(),
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "active" | "pending" | "approved"
  >("all");
  const [expandedEntryId, setExpandedEntryId] = useState<string | null>(null);
  const gracePeriodMinutes = currentUser?.gracePeriodMinutes ?? 5;
  const defaultRange = getDefaultDateRange();
  const [dateFrom, setDateFrom] = useState(defaultRange.start);
  const [dateTo, setDateTo] = useState(defaultRange.end);
  const [reopenConfirmId, setReopenConfirmId] = useState<string | null>(null);
  const [reopenLoading, setReopenLoading] = useState(false);
  const [showManualEntryModal, setShowManualEntryModal] = useState(false);

  useEffect(() => {
    if (currentUser?.uid) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);

  const loadData = async (from = dateFrom, to = dateTo) => {
    if (!currentUser?.uid || !currentUser?.companyId) return;
    setLoading(true);
    try {
      const isCompany = currentUser.userType === 'company';
      const staffMembers = await (isCompany
        ? getCompanyUsers(currentUser.companyId)
        : getManagerStaff(currentUser.uid));
      setStaff(staffMembers);
      if (staffMembers.length > 0) {
        const staffIds = staffMembers.map((s) => s.uid);
        const entries = await getStaffTimeEntries(
          staffIds,
          currentUser.companyId,
          new Date(from + "T00:00:00"),
          new Date(to + "T23:59:59"),
          isCompany ? undefined : currentUser.uid,
        );
        setTimeEntries(entries);
      }
    } catch (error) {
      console.error("Error loading time entries:", error);
    } finally {
      setLoading(false);
    }
  };

  const staffMap = useMemo(
    () => new Map(staff.map((s) => [s.uid, s])),
    [staff],
  );

  const filteredTimeEntries = timeEntries.filter((entry) => {
    if (statusFilter !== "all" && entry.status !== statusFilter) return false;
    if (searchTerm) {
      const staffMember = staffMap.get(entry.userId);
      const name =
        `${staffMember?.firstName} ${staffMember?.lastName}`.toLowerCase();
      return name.includes(searchTerm.toLowerCase());
    }
    return true;
  });

  const toggleEntrySelection = (entryId: string) => {
    const newSelected = new Set(selectedEntries);
    if (newSelected.has(entryId)) {
      newSelected.delete(entryId);
    } else {
      newSelected.add(entryId);
    }
    setSelectedEntries(newSelected);
  };

  const handleApproveEntry = async (entryId: string) => {
    try {
      await approveTimeEntry(entryId);
      await loadData();
    } catch (error) {
      console.error("Error approving entry:", error);
      showToast("Failed to approve time entry", "error");
    }
  };

  const handleReopenEntry = async () => {
    if (!reopenConfirmId) return;
    setReopenLoading(true);
    try {
      await reopenTimeEntry(reopenConfirmId);
      setReopenConfirmId(null);
      await loadData();
    } catch (error) {
      console.error("Error reopening entry:", error);
      showToast("Failed to reopen time entry", "error");
    } finally {
      setReopenLoading(false);
    }
  };

  const handleBulkApprove = async () => {
    if (selectedEntries.size === 0) return;
    try {
      await bulkApproveTimeEntries(Array.from(selectedEntries));
      setSelectedEntries(new Set());
      await loadData();
    } catch (error) {
      console.error("Error bulk approving:", error);
      showToast("Failed to approve time entries", "error");
    }
  };

  const exportToCSV = () => {
    const headers = [
      "Staff Name",
      "Date",
      "Clock In",
      "Clock Out",
      "Hours",
      "Status",
    ];
    const rows = filteredTimeEntries.map((entry) => {
      const staffMember = staffMap.get(entry.userId);
      const staffName = staffMember
        ? `${staffMember.firstName} ${staffMember.lastName}`
        : "Unknown";
      return [
        staffName,
        entry.actualClockIn.toLocaleDateString("en-IE", { ...(tz && { timeZone: tz }) }),
        entry.actualClockIn.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false, ...(tz && { timeZone: tz }) }),
        entry.actualClockOut?.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false, ...(tz && { timeZone: tz }) }) || "Active",
        entry.paidHours.toFixed(2),
        entry.status,
      ];
    });
    const csv = [headers, ...rows].map((row) => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `timesheet-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div
        style={{
          padding: SPACING["2xl"],
          textAlign: "center",
          color: COLORS.text.secondary,
        }}
      >
        Loading time entries...
      </div>
    );
  }

  return (
    <div style={{ padding: SPACING["2xl"] }}>
      <PageHeader
        title="Time Entries"
        subtitle={currentUser?.userType === 'company' ? "Review and manage all time entries across your company" : "Review and approve your team's time entries"}
        actions={
          <div style={{ display: "flex", gap: "8px" }}>
            <QuickActionButton
              label="Add Manual Entry"
              icon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19" />
                  <line x1="5" y1="12" x2="19" y2="12" />
                </svg>
              }
              onClick={() => setShowManualEntryModal(true)}
              variant="primary"
            />
            <QuickActionButton
              label="Export CSV"
              icon={
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              }
              onClick={exportToCSV}
              variant="secondary"
            />
          </div>
        }
      />

      {/* Filters */}
      <div
        style={{
          marginBottom: SPACING.xl,
          display: "flex",
          gap: SPACING.md,
          flexWrap: "wrap",
        }}
      >
        <input
          type="text"
          placeholder="Search staff..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{
            flex: "1",
            minWidth: "200px",
            padding: `${SPACING.sm} ${SPACING.lg}`,
            fontSize: TYPOGRAPHY.fontSize.sm,
            border: `1px solid ${COLORS.border.medium}`,
            borderRadius: BORDER_RADIUS.md,
            outline: "none",
          }}
          onFocus={(e) => {
            e.currentTarget.style.borderColor = COLORS.primary.main;
          }}
          onBlur={(e) => {
            e.currentTarget.style.borderColor = COLORS.border.medium;
          }}
        />
        <select
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(
              e.target.value as "all" | "active" | "pending" | "approved",
            )
          }
          style={{
            ...SELECT_CHEVRON_STYLE,
            padding: `${SPACING.sm} 36px ${SPACING.sm} ${SPACING.lg}`,
            fontSize: TYPOGRAPHY.fontSize.sm,
            border: `1px solid ${COLORS.border.medium}`,
            borderRadius: BORDER_RADIUS.md,
            outline: "none",
          }}
        >
          <option value="all">All Status</option>
          <option value="active">Clocked In</option>
          <option value="pending">Pending Approval</option>
          <option value="approved">Approved</option>
        </select>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          style={{
            padding: `${SPACING.sm} ${SPACING.lg}`,
            fontSize: TYPOGRAPHY.fontSize.sm,
            border: `1px solid ${COLORS.border.medium}`,
            borderRadius: BORDER_RADIUS.md,
            outline: "none",
          }}
        />
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          style={{
            padding: `${SPACING.sm} ${SPACING.lg}`,
            fontSize: TYPOGRAPHY.fontSize.sm,
            border: `1px solid ${COLORS.border.medium}`,
            borderRadius: BORDER_RADIUS.md,
            outline: "none",
          }}
        />
        <button
          onClick={() => loadData(dateFrom, dateTo)}
          style={{
            padding: `${SPACING.sm} ${SPACING.lg}`,
            fontSize: TYPOGRAPHY.fontSize.sm,
            fontWeight: TYPOGRAPHY.fontWeight.medium,
            color: COLORS.text.inverse,
            backgroundColor: COLORS.primary.main,
            border: "none",
            borderRadius: BORDER_RADIUS.md,
            cursor: "pointer",
          }}
        >
          Apply
        </button>
      </div>

      {/* Bulk Actions */}
      {selectedEntries.size > 0 && (
        <div
          style={{
            backgroundColor: COLORS.primary.light,
            padding: SPACING.lg,
            borderRadius: BORDER_RADIUS.md,
            marginBottom: SPACING.lg,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span
            style={{
              fontSize: TYPOGRAPHY.fontSize.sm,
              fontWeight: TYPOGRAPHY.fontWeight.medium,
              color: COLORS.primary.dark,
            }}
          >
            {selectedEntries.size}{" "}
            {selectedEntries.size === 1 ? "entry" : "entries"} selected
          </span>
          <QuickActionButton
            label="Approve Selected"
            icon={
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            }
            onClick={handleBulkApprove}
            variant="primary"
          />
        </div>
      )}

      {/* Table */}
      <div
        style={{
          backgroundColor: COLORS.background.primary,
          borderRadius: BORDER_RADIUS.xl,
          border: `1px solid ${COLORS.border.light}`,
          overflow: "auto",
        }}
      >
        {filteredTimeEntries.length === 0 ? (
          <div style={{ padding: SPACING["2xl"], textAlign: "center" }}>
            <p
              style={{
                fontSize: TYPOGRAPHY.fontSize.base,
                color: COLORS.text.secondary,
              }}
            >
              No time entries found.
            </p>
          </div>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead style={{ backgroundColor: COLORS.background.secondary }}>
              <tr>
                <th style={{ padding: SPACING.lg, width: "40px" }}></th>
                {[
                  "Staff",
                  "Job",
                  "Date",
                  "Clock In",
                  "Clock Out",
                  "Hours",
                  "Flags",
                  "Correction Note",
                  "Status",
                  "Approved By",
                ].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: SPACING.lg,
                      textAlign: "left",
                      fontSize: TYPOGRAPHY.fontSize.sm,
                      fontWeight: TYPOGRAPHY.fontWeight.semibold,
                      color: COLORS.text.secondary,
                    }}
                  >
                    {h}
                  </th>
                ))}
                <th style={{ padding: SPACING.lg, width: "32px" }}></th>
              </tr>
            </thead>
            <tbody>
              {filteredTimeEntries.map((entry) => {
                const staffMember = staffMap.get(entry.userId);
                const isExpanded = expandedEntryId === entry.id;
                type Adj = {
                  approvedBy?: string;
                  approvedAt?: Date;
                  correctionReason?: string;
                  originalClockIn?: Date;
                  originalClockOut?: Date;
                  correctedClockIn?: Date;
                  correctedClockOut?: Date;
                };
                const adj = (entry as TimeEntry & { managerAdjustments?: Adj })
                  .managerAdjustments;
                const wasCorrected = !!adj?.correctedClockIn;
                return (
                  <>
                    <tr
                      key={entry.id}
                      onClick={() =>
                        setExpandedEntryId(isExpanded ? null : entry.id)
                      }
                      style={{
                        borderTop: `1px solid ${COLORS.border.light}`,
                        cursor: "pointer",
                        backgroundColor: isExpanded
                          ? COLORS.background.secondary
                          : "transparent",
                      }}
                    >
                      <td
                        style={{ padding: SPACING.lg }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedEntries.has(entry.id)}
                          onChange={() => toggleEntrySelection(entry.id)}
                          style={{ cursor: "pointer" }}
                        />
                      </td>
                      <td
                        style={{
                          padding: SPACING.lg,
                          fontSize: TYPOGRAPHY.fontSize.sm,
                          color: COLORS.text.primary,
                          fontWeight: TYPOGRAPHY.fontWeight.medium,
                        }}
                      >
                        {staffMember
                          ? `${staffMember.firstName} ${staffMember.lastName}`
                          : "Unknown"}
                      </td>
                      <td
                        style={{
                          padding: SPACING.lg,
                          fontSize: TYPOGRAPHY.fontSize.sm,
                          color: COLORS.text.secondary,
                        }}
                      >
                        {(entry as TimeEntry & { jobTitle?: string })
                          .jobTitle || "—"}
                      </td>
                      <td
                        style={{
                          padding: SPACING.lg,
                          fontSize: TYPOGRAPHY.fontSize.sm,
                          color: COLORS.text.secondary,
                        }}
                      >
                        {entry.actualClockIn.toLocaleDateString("en-IE", { ...(tz && { timeZone: tz }) })}
                      </td>
                      <td
                        style={{
                          padding: SPACING.lg,
                          fontSize: TYPOGRAPHY.fontSize.sm,
                          color: COLORS.text.secondary,
                        }}
                      >
                        {entry.actualClockIn.toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                          ...(tz && { timeZone: tz }),
                        })}
                        {(entry.clockInVariance || 0) !== 0 && (
                          <span
                            style={{
                              marginLeft: 4,
                              fontSize: TYPOGRAPHY.fontSize.xs,
                              color:
                                (entry.clockInVariance || 0) > 0
                                  ? COLORS.danger.main
                                  : COLORS.text.tertiary,
                            }}
                          >
                            ({(entry.clockInVariance || 0) > 0 ? "+" : ""}
                            {Math.round(entry.clockInVariance || 0)}min)
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          padding: SPACING.lg,
                          fontSize: TYPOGRAPHY.fontSize.sm,
                          color: COLORS.text.secondary,
                        }}
                      >
                        {entry.actualClockOut
                          ? entry.actualClockOut.toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: false,
                              ...(tz && { timeZone: tz }),
                            })
                          : "—"}
                        {entry.actualClockOut &&
                          (entry.clockOutVariance || 0) !== 0 && (
                            <span
                              style={{
                                marginLeft: 4,
                                fontSize: TYPOGRAPHY.fontSize.xs,
                                color:
                                  (entry.clockOutVariance || 0) < 0
                                    ? COLORS.danger.main
                                    : COLORS.text.tertiary,
                              }}
                            >
                              ({(entry.clockOutVariance || 0) > 0 ? "+" : ""}
                              {Math.round(entry.clockOutVariance || 0)}min)
                            </span>
                          )}
                      </td>
                      <td
                        style={{
                          padding: SPACING.lg,
                          fontSize: TYPOGRAPHY.fontSize.sm,
                          color: COLORS.text.secondary,
                        }}
                      >
                        {entry.status === "active"
                          ? "—"
                          : `${entry.paidHours.toFixed(2)}h`}
                      </td>
                      <td style={{ padding: SPACING.lg }}>
                        <div
                          style={{
                            display: "flex",
                            gap: SPACING.xs,
                            flexWrap: "wrap",
                          }}
                        >
                          {(entry.clockInVariance || 0) >
                            gracePeriodMinutes && (
                            <Flag label="Late arrival" color={COLORS.warning} />
                          )}
                          {(entry.clockOutVariance || 0) <
                            -gracePeriodMinutes && (
                            <Flag
                              label="Early departure"
                              color={COLORS.warning}
                            />
                          )}
                          {entry.scheduledHours != null &&
                            entry.actualHours != null &&
                            entry.actualHours <
                              entry.scheduledHours -
                                gracePeriodMinutes / 60 && (
                              <Flag label="Short hours" color={COLORS.danger} />
                            )}
                          {!(
                            (entry.clockInVariance || 0) > gracePeriodMinutes
                          ) &&
                            !(
                              (entry.clockOutVariance || 0) <
                              -gracePeriodMinutes
                            ) &&
                            !(
                              entry.scheduledHours != null &&
                              entry.actualHours != null &&
                              entry.actualHours <
                                entry.scheduledHours - gracePeriodMinutes / 60
                            ) &&
                            entry.status !== "active" && (
                              <span
                                style={{
                                  fontSize: TYPOGRAPHY.fontSize.xs,
                                  color: COLORS.text.secondary,
                                }}
                              >
                                On time
                              </span>
                            )}
                        </div>
                      </td>
                      <td style={{ padding: SPACING.lg, maxWidth: 160 }}>
                        {adj?.correctionReason ? (
                          <span
                            title={adj.correctionReason}
                            style={{
                              fontSize: TYPOGRAPHY.fontSize.xs,
                              color: COLORS.text.secondary,
                              fontStyle: "italic",
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {adj.correctionReason}
                          </span>
                        ) : (
                          <span
                            style={{
                              fontSize: TYPOGRAPHY.fontSize.xs,
                              color: COLORS.text.tertiary,
                            }}
                          >
                            —
                          </span>
                        )}
                      </td>
                      <td style={{ padding: SPACING.lg }}>
                        <span
                          style={{
                            padding: `${SPACING.xs} ${SPACING.md}`,
                            borderRadius: BORDER_RADIUS.full,
                            fontSize: TYPOGRAPHY.fontSize.xs,
                            fontWeight: TYPOGRAPHY.fontWeight.medium,
                            whiteSpace: "nowrap",
                            display: "inline-block",
                            textAlign: "center",
                            minWidth: 90,
                            backgroundColor:
                              entry.status === "approved"
                                ? COLORS.success.light
                                : entry.status === "active"
                                  ? COLORS.primary.light
                                  : COLORS.warning.light,
                            color:
                              entry.status === "approved"
                                ? COLORS.success.dark
                                : entry.status === "active"
                                  ? COLORS.primary.dark
                                  : COLORS.warning.dark,
                          }}
                        >
                          {entry.status === "active"
                            ? "Clocked In"
                            : entry.status === "pending"
                              ? "Pending"
                              : "Approved"}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: SPACING.lg,
                          fontSize: TYPOGRAPHY.fontSize.sm,
                          color: COLORS.text.secondary,
                        }}
                      >
                        {entry.status === "approved" && adj?.approvedBy
                          ? (() => {
                              const u = staffMap.get(adj.approvedBy as string);
                              if (u) return `${u.firstName} ${u.lastName}`;
                              if (adj.approvedBy === currentUser?.uid)
                                return `${currentUser.firstName} ${currentUser.lastName} (you)`;
                              return adj.approvedBy as string;
                            })()
                          : "—"}
                      </td>
                      <td style={{ padding: SPACING.lg }}>
                        <div
                          style={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            backgroundColor: isExpanded
                              ? COLORS.primary.light
                              : COLORS.background.secondary,
                            border: `1px solid ${isExpanded ? COLORS.primary.main : COLORS.border.medium}`,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: isExpanded
                              ? COLORS.primary.main
                              : COLORS.text.secondary,
                            fontSize: TYPOGRAPHY.fontSize.xs,
                            transition: "all 0.15s",
                          }}
                        >
                          {isExpanded ? "▲" : "▼"}
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr
                        key={`${entry.id}-detail`}
                        style={{ backgroundColor: COLORS.background.secondary }}
                      >
                        <td
                          colSpan={12}
                          style={{
                            padding: `${SPACING.md} ${SPACING.xl} ${SPACING.xl}`,
                          }}
                        >
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(220px, 1fr))",
                              gap: SPACING.lg,
                            }}
                          >
                            <DetailBlock label="Scheduled">
                              {entry.scheduledHours != null
                                ? `${entry.scheduledHours.toFixed(2)}h`
                                : "—"}
                            </DetailBlock>
                            <DetailBlock label="Actual Hours">
                              {entry.actualHours != null
                                ? `${entry.actualHours.toFixed(2)}h`
                                : "—"}
                            </DetailBlock>
                            <DetailBlock label="Paid Hours">
                              {entry.paidHours.toFixed(2)}h
                            </DetailBlock>
                            <DetailBlock label="Hourly Rate">
                              €{entry.hourlyRate.toFixed(2)}
                            </DetailBlock>
                            {wasCorrected && (
                              <>
                                <DetailBlock label="Original Clock In">
                                  {adj!.originalClockIn instanceof Date
                                    ? adj!.originalClockIn.toLocaleTimeString(
                                        [],
                                        { hour: "2-digit", minute: "2-digit", hour12: false, ...(tz && { timeZone: tz }) },
                                      )
                                    : "—"}
                                </DetailBlock>
                                <DetailBlock label="Original Clock Out">
                                  {adj!.originalClockOut instanceof Date
                                    ? adj!.originalClockOut.toLocaleTimeString(
                                        [],
                                        { hour: "2-digit", minute: "2-digit", hour12: false, ...(tz && { timeZone: tz }) },
                                      )
                                    : "—"}
                                </DetailBlock>
                                <DetailBlock label="Corrected Clock In">
                                  {adj!.correctedClockIn instanceof Date
                                    ? adj!.correctedClockIn.toLocaleTimeString(
                                        [],
                                        { hour: "2-digit", minute: "2-digit", hour12: false, ...(tz && { timeZone: tz }) },
                                      )
                                    : "—"}
                                </DetailBlock>
                                <DetailBlock label="Corrected Clock Out">
                                  {adj!.correctedClockOut instanceof Date
                                    ? adj!.correctedClockOut.toLocaleTimeString(
                                        [],
                                        { hour: "2-digit", minute: "2-digit", hour12: false, ...(tz && { timeZone: tz }) },
                                      )
                                    : "—"}
                                </DetailBlock>
                              </>
                            )}
                            {adj?.correctionReason && (
                              <DetailBlock label="Correction Reason" wide>
                                {adj.correctionReason}
                              </DetailBlock>
                            )}
                            {adj?.approvedBy && (
                              <DetailBlock label="Approved By">
                                {(() => {
                                  const u = staffMap.get(
                                    adj.approvedBy as string,
                                  );
                                  if (u) return `${u.firstName} ${u.lastName}`;
                                  if (adj.approvedBy === currentUser?.uid)
                                    return `${currentUser.firstName} ${currentUser.lastName} (you)`;
                                  return adj.approvedBy as string;
                                })()}
                              </DetailBlock>
                            )}
                            {adj?.approvedAt instanceof Date && (
                              <DetailBlock label="Approved At">
                                {adj.approvedAt.toLocaleString("en-IE", {
                                  dateStyle: "medium",
                                  timeStyle: "short",
                                  hour12: false,
                                  ...(tz && { timeZone: tz }),
                                })}
                              </DetailBlock>
                            )}
                            {entry.jobAddress && (
                              <DetailBlock label="Location" wide>
                                {entry.jobAddress}
                              </DetailBlock>
                            )}
                          </div>
                          {entry.status === "pending" && (
                            <div style={{ marginTop: SPACING.lg }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApproveEntry(entry.id);
                                }}
                                style={{
                                  padding: `${SPACING.sm} ${SPACING.xl}`,
                                  fontSize: TYPOGRAPHY.fontSize.sm,
                                  fontWeight: TYPOGRAPHY.fontWeight.medium,
                                  color: COLORS.text.inverse,
                                  backgroundColor: COLORS.success.main,
                                  border: "none",
                                  borderRadius: BORDER_RADIUS.md,
                                  cursor: "pointer",
                                }}
                              >
                                Approve
                              </button>
                            </div>
                          )}
                          {entry.status === "approved" && (
                            <div style={{ marginTop: SPACING.lg }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReopenConfirmId(entry.id);
                                }}
                                style={{
                                  padding: `${SPACING.sm} ${SPACING.xl}`,
                                  fontSize: TYPOGRAPHY.fontSize.sm,
                                  fontWeight: TYPOGRAPHY.fontWeight.medium,
                                  color: COLORS.text.secondary,
                                  backgroundColor: "transparent",
                                  border: `1px solid ${COLORS.border.medium}`,
                                  borderRadius: BORDER_RADIUS.md,
                                  cursor: "pointer",
                                }}
                              >
                                Reopen for Correction
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ConfirmationDialog
        isOpen={!!reopenConfirmId}
        title="Reopen for Correction?"
        message="This entry will return to Pending status and can be corrected and re-approved."
        confirmLabel="Reopen"
        confirmStyle="warning"
        loading={reopenLoading}
        onConfirm={handleReopenEntry}
        onCancel={() => setReopenConfirmId(null)}
      />

      <CreateManualTimeEntryModal
        isOpen={showManualEntryModal}
        onClose={() => setShowManualEntryModal(false)}
        companyId={currentUser?.companyId ?? ''}
        managerId={currentUser?.userType === 'manager' ? currentUser.uid : undefined}
        staff={staff}
        companyTimezone={tz}
        onSuccess={() => {
          setShowManualEntryModal(false);
          loadData();
        }}
      />

      <Toast toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
}
